import { createSlice } from '@reduxjs/toolkit';
import { successMessages } from 'constants/errors';
import { ROOT_HIERARCHY_ID } from 'constants/hierarchy';
import { EntityStorages } from 'types';

import {
  createHierarchy,
  deleteHierarchyNode,
  insertMapEntities,
  insertMapEntity,
  mergeMaps,
  notify,
  removeMapEntity,
  updateHierarchyProperty,
} from 'utils';

import { LAYERS_REDUCER_NAMES } from '../constants';

import {
  addMediaToEntityThunk,
  createMapEntityThunk,
  createQuickMapEntityThunk,
  deleteEntityThunk,
  getAllMapEntitiesThunk,
  getFilteredMapEntitiesThunk,
  getInitialMapEntitiesThunk,
  getInscribedMapEntities,
  getPredefinedTemplateThunk,
  getSharedMapEntityThunk,
  importMapEntitiesThunk,
  openMapFolderEntityThunk,
  regroupMapEntitiesThunk,
  relinkEntityThunk,
  selectMapFolderEntityThunk,
  updateMapEntitiesBulkThunk,
  updateMapEntityThunk,
} from './actions';
import { initialState } from './constants';
import {
  SetCountersAction,
  SetEntitiesAction,
  SetHierarchyAction,
  SetStateAction,
} from './types';
import {
  decreaseMapEntityObjectCounters,
  getMapEntities,
  getMapEntityTemplateIds,
  handleMapEntitiesBulkUpdate,
  handleMapEntitiesGet,
  handleMapEntityChildrenGet,
  handleMapEntityCreate,
  insertCounters,
  processExistingMapEntity,
  processInscribedMapEntity,
  processOpenedMapFolderEntityChild,
  processSharedMapEntity,
} from './utils';

const mapEntitiesSlice = createSlice({
  name: LAYERS_REDUCER_NAMES.MAP_ENTITIES,
  initialState: initialState,
  reducers: {
    setState(state, action: SetStateAction) {
      return action.payload;
    },
    setEntities(state, action: SetEntitiesAction) {
      state.entities = action.payload;
    },
    setHierarchy(state, action: SetHierarchyAction) {
      state.hierarchy = action.payload;
    },
    setCounters(state, action: SetCountersAction) {
      state.counters = action.payload;
    },
    resetState() {
      return initialState;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(getPredefinedTemplateThunk.fulfilled, (state, action) => {
        state.predefinedTemplates[action.payload.template.title] =
          action.payload;
      })
      .addCase(getPredefinedTemplateThunk.rejected, (state, action) => {
        notify.error(action.payload?.message);
      })
      .addCase(getInitialMapEntitiesThunk.fulfilled, (state, action) => {
        const { entities, hierarchy, counters } = handleMapEntitiesGet(
          state,
          action.payload,
          { storage: EntityStorages.NONE, active: false }
        );

        state.entities = entities;
        state.hierarchy = hierarchy;
        state.counters = counters ?? {};
      })
      .addCase(getInitialMapEntitiesThunk.rejected, (state, action) => {
        notify.error(action.payload?.message);
      })
      .addCase(getAllMapEntitiesThunk.fulfilled, (state, action) => {
        const { processEntityFn } = action.meta.arg;
        const { entities, hierarchy, counters } = handleMapEntitiesGet(
          state,
          action.payload,
          {
            storage: EntityStorages.FULL,
            active: false,
          },
          processEntityFn,
          true
        );

        state.entities = entities;
        state.hierarchy = hierarchy;
        state.counters = counters ?? {};
      })
      .addCase(getAllMapEntitiesThunk.rejected, (state, action) => {
        notify.error(action.payload?.message);
      })
      .addCase(getFilteredMapEntitiesThunk.fulfilled, (state, action) => {
        const {
          filterCriteria,
          createdAtFilter,
          query,
          idQuery,
          desiredEntityState,
        } = action.meta.arg;
        const { entities, hierarchy, counters } = action.payload;
        const { mapLayerTemplateId, mapObjectTemplateId } =
          getMapEntityTemplateIds(state.predefinedTemplates);

        const isFiltering = !!(
          filterCriteria?.length ||
          query ||
          idQuery ||
          createdAtFilter
        );

        const filteredEntities = getMapEntities(
          mapLayerTemplateId,
          mapObjectTemplateId,
          entities,
          {
            storage: EntityStorages.NONE,
            active: !!desiredEntityState?.active,
          }
        );

        const stashedEntities = {
          ...state.stash.entities,
          ...state.entities,
        };

        const updatedEntities = mergeMaps(
          filteredEntities,
          stashedEntities,
          !isFiltering,
          (oldEntity, newEntity) => ({
            ...oldEntity,
            childIDs: newEntity.childIDs,
            state: {
              ...newEntity.state,
              ...(desiredEntityState?.active
                ? { active: desiredEntityState?.active }
                : {}),
              storage: oldEntity.state.storage,
            },
          })
        );

        state.entities = updatedEntities;
        state.hierarchy = createHierarchy(ROOT_HIERARCHY_ID, hierarchy);
        state.counters = insertCounters(state.counters, counters);
        state.stash = {
          ...state.stash,
          entities: isFiltering ? stashedEntities : {},
        };
      })
      .addCase(getFilteredMapEntitiesThunk.rejected, (state, action) => {
        notify.error(action.payload?.message);
      })
      .addCase(getSharedMapEntityThunk.fulfilled, (state, action) => {
        const { entities, hierarchy, targetId } = action.payload;
        const { mapLayerTemplateId, mapObjectTemplateId } =
          getMapEntityTemplateIds(state.predefinedTemplates);

        const { id, children } = hierarchy?.[0] ?? initialState.hierarchy;

        const sharedMapEntities = getMapEntities(
          mapLayerTemplateId,
          mapObjectTemplateId,
          entities,
          { storage: EntityStorages.NONE, active: false },
          (mapEntity) => processSharedMapEntity(mapEntity, entities, targetId)
        );

        state.entities = { ...state.entities, ...sharedMapEntities };

        state.hierarchy = updateHierarchyProperty(
          state.hierarchy,
          id,
          'children',
          children
        );
      })
      .addCase(getSharedMapEntityThunk.rejected, (state, action) => {
        notify.error(action.payload?.message);
      })
      .addCase(getInscribedMapEntities.fulfilled, (state, action) => {
        const { entities } = action.payload;
        const { mapLayerTemplateId, mapObjectTemplateId } =
          getMapEntityTemplateIds(state.predefinedTemplates);

        const updatedEntities = getMapEntities(
          mapLayerTemplateId,
          mapObjectTemplateId,
          entities,
          { storage: EntityStorages.NONE, active: false },
          (mapEntity) => processInscribedMapEntity(mapEntity, state.entities)
        );

        state.entities = { ...state.entities, ...updatedEntities };
      })
      .addCase(getInscribedMapEntities.rejected, (state, action) => {
        notify.error(action.payload?.message);
      })
      .addCase(openMapFolderEntityThunk.fulfilled, (state, action) => {
        const { parentEntityID } = action.meta.arg;

        const updatedState = handleMapEntityChildrenGet(
          state,
          {
            ...action.payload,
            parentId: parentEntityID,
          },
          { storage: EntityStorages.PARTIAL, active: false },
          processOpenedMapFolderEntityChild
        );

        /* 
            /entity/search (without parentEntityID) returns all actual counters at once so no need to do this yet
            state.counters = updatedState.counters;
          */
        state.entities = updatedState.entities;
        state.hierarchy = updatedState.hierarchy;
      })
      .addCase(openMapFolderEntityThunk.rejected, (state, action) => {
        notify.error(action.payload?.message);
      })
      .addCase(selectMapFolderEntityThunk.fulfilled, (state, action) => {
        const { parentEntityID } = action.meta.arg;

        const updatedState = handleMapEntityChildrenGet(
          state,
          {
            ...action.payload,
            parentId: parentEntityID,
          },
          { storage: EntityStorages.FULL, active: true }
        );

        state.entities = updatedState.entities;
        state.hierarchy = updatedState.hierarchy;
      })
      .addCase(selectMapFolderEntityThunk.rejected, (state, action) => {
        notify.error(action.payload?.message);
      })
      .addCase(createMapEntityThunk.fulfilled, (state, action) => {
        const updatedState = handleMapEntityCreate(state, action.payload);

        state.entities = updatedState.entities;
        state.hierarchy = updatedState.hierarchy;
        state.counters = updatedState.counters;

        notify.success(successMessages.ENTITY_CREATION_SUCCESS);
      })
      .addCase(createMapEntityThunk.rejected, (state, action) => {
        notify.error(action.payload?.message);
      })
      .addCase(createQuickMapEntityThunk.fulfilled, (state, action) => {
        const updatedState = handleMapEntityCreate(state, action.payload);

        state.entities = updatedState.entities;
        state.hierarchy = updatedState.hierarchy;
        state.counters = updatedState.counters;
      })
      .addCase(createQuickMapEntityThunk.rejected, (state, action) => {
        notify.error(action.payload?.message);
      })
      .addCase(importMapEntitiesThunk.fulfilled, (state, action) => {
        const {
          params: { entityID },
        } = action.meta.arg;
        const { entities, hierarchy, counters } = action.payload;
        const { mapLayerTemplateId, mapObjectTemplateId } =
          getMapEntityTemplateIds(state.predefinedTemplates);

        const { id, children } = hierarchy?.[0] ?? initialState.hierarchy;
        const currentEntityId = entityID ?? id;

        if (currentEntityId === ROOT_HIERARCHY_ID) {
          const mapEntityChildren = getMapEntities(
            mapLayerTemplateId,
            mapObjectTemplateId,
            entities,
            { storage: EntityStorages.NONE, active: false },
            (mapEntity) =>
              processExistingMapEntity(state.entities, mapEntity, {
                storage: true,
                active: true,
              })
          );

          state.entities = mapEntityChildren;
          state.hierarchy = createHierarchy(ROOT_HIERARCHY_ID, hierarchy);
        } else {
          const mapEntityChildren = getMapEntities(
            mapLayerTemplateId,
            mapObjectTemplateId,
            entities,
            { storage: EntityStorages.NONE, active: false },
            (mapEntity) =>
              processExistingMapEntity(state.entities, mapEntity, {
                storage: true,
                active: true,
              })
          );

          state.entities = {
            ...state.entities,
            ...mapEntityChildren,
          };

          state.hierarchy = updateHierarchyProperty(
            state.hierarchy,
            currentEntityId,
            'children',
            children
          );
        }

        state.counters = insertCounters(state.counters, counters);
      })
      .addCase(importMapEntitiesThunk.rejected, (state, action) => {
        notify.error(action.payload?.message);
      })
      .addCase(updateMapEntityThunk.fulfilled, (state, action) => {
        const updatedEntity = action.payload;

        state.entities = insertMapEntity(state.entities, updatedEntity);
      })
      .addCase(updateMapEntityThunk.rejected, (state, action) => {
        notify.error(action.payload?.message);
      })
      .addCase(updateMapEntitiesBulkThunk.fulfilled, (state, action) => {
        const { parentEntityID } = action.meta.arg;

        const updatedState = handleMapEntitiesBulkUpdate(state, {
          ...action.payload,
          parentId: parentEntityID,
        });

        state.entities = updatedState.entities;
        state.hierarchy = updatedState.hierarchy;
      })
      .addCase(updateMapEntitiesBulkThunk.rejected, (state, action) => {
        notify.error(action.payload?.message);
      })
      .addCase(regroupMapEntitiesThunk.fulfilled, (state, action) => {
        const updatedState = handleMapEntitiesBulkUpdate(state, action.payload);

        state.entities = updatedState.entities;
        state.hierarchy = updatedState.hierarchy;
      })
      .addCase(regroupMapEntitiesThunk.rejected, (state, action) => {
        notify.error(action.payload?.message);
      })
      .addCase(relinkEntityThunk.fulfilled, (state, action) => {
        const { entities, hierarchy, counters } = action.payload;

        const { mapLayerTemplateId, mapObjectTemplateId } =
          getMapEntityTemplateIds(state.predefinedTemplates);

        const updatedEntities = getMapEntities(
          mapLayerTemplateId,
          mapObjectTemplateId,
          entities,
          { storage: EntityStorages.NONE, active: false },
          (mapEntity) =>
            processExistingMapEntity(state.entities, mapEntity, {
              storage: true,
              active: true,
            })
        );

        const updatedHierarchy =
          hierarchy?.reduce(
            (acc, curr) =>
              updateHierarchyProperty(acc, curr.id, 'children', curr.children),
            state.hierarchy
          ) ?? state.hierarchy;

        state.entities = insertMapEntities(state.entities, updatedEntities);
        state.hierarchy = updatedHierarchy;
        state.counters = insertCounters(state.counters, counters);
      })
      .addCase(relinkEntityThunk.rejected, (state, action) => {
        notify.error(action.payload?.message);
      })
      .addCase(addMediaToEntityThunk.fulfilled, (state, action) => {
        const updatedEntity = action.payload;

        state.entities = insertMapEntity(state.entities, updatedEntity);
      })
      .addCase(addMediaToEntityThunk.rejected, (state, action) => {
        notify.error(action.payload?.message);
      })
      .addCase(deleteEntityThunk.fulfilled, (state, action) => {
        const deletedEntityID = action.meta.arg;
        const { mapLayerTemplateId, mapObjectTemplateId } =
          getMapEntityTemplateIds(state.predefinedTemplates);

        const deletedEntity = state.entities[deletedEntityID];
        const updatedEntities = removeMapEntity(
          state.entities,
          deletedEntityID
        );

        const updatedHierarchy = deleteHierarchyNode(
          state.hierarchy,
          deletedEntityID,
          deletedEntity.parentIDs[0]
        );

        const updatedCounters = decreaseMapEntityObjectCounters(
          state.entities,
          state.counters,
          deletedEntity.entity.id,
          mapLayerTemplateId,
          mapObjectTemplateId
        );

        state.entities = updatedEntities;

        if (deletedEntity?.parentIDs?.length) {
          state.hierarchy = updatedHierarchy;
        }

        state.counters = updatedCounters;
      })
      .addCase(deleteEntityThunk.rejected, (state, action) => {
        notify.error(action.payload?.message);
      }),
});

export const { actions: mapEntitiesActions, reducer: mapEntitiesReducer } =
  mapEntitiesSlice;
